import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shouldRedirectOnLogout } from '@/components/auth';
import { useCurrentUserContext } from '@/contexts';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { useLogout } from '@/hooks/useLogout';
import { useOrganizations } from '@/hooks/useOrganizations';
import { OrganizationRole, OrganizationType } from '@/types/gql.generated';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Divider,
  Button,
  Icon,
  IconButton,
  MenuDivider,
  Text,
} from '@/ui';
import { getOrgTypeIcon } from '@/utils/organization';
import { useSettingsModal } from '../store';
import { AccountListItem } from './AccountListItem';
import { CreateOrganizationItem } from './CreateOrganizationItem';
import { Tab } from './Tab';

export const Navigation = () => {
  const { tab, organizationId, close, changeTab } = useSettingsModal();
  const isMobileBreakpoint = useIsMobileBreakpoint();
  const { t } = useTranslation(['settingsModal', 'auth']);
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserContext();
  // Every shared org the user belongs to gets its own entry, independent of
  // the active library context.
  const { sharedOrganizations } = useOrganizations();

  const personalAccountName =
    currentUser?.name || currentUser?.email || t('navigation.personal_account');

  const getOrgSubtitle = (type: OrganizationType) =>
    type === OrganizationType.Family
      ? t('navigation.org_type_family')
      : t('navigation.org_type_business');

  const getRoleLabel = (role: OrganizationRole) =>
    role === OrganizationRole.Owner
      ? t('navigation.role_owner')
      : t('navigation.role_member');

  // The personal account entry surfaces the repurposed plan/billing panel.
  const handlePersonalAccountClick = () => {
    changeTab('subscription');
  };

  const handleAccountClick = () => {
    changeTab('account');
  };

  const handleHelpClick = () => {
    changeTab('help');
  };

  const handleCalendarsClick = () => {
    changeTab('calendars');
  };

  const handleIntegrationsClick = () => {
    changeTab('integrations');
  };

  const handleCreateOrganizationClick = () => {
    changeTab('createOrganization');
  };

  const handleLogoutClick = () => {
    logout({
      onSuccess: () => {
        close();

        const { shouldRedirect, path } = shouldRedirectOnLogout();
        if (shouldRedirect) {
          navigate(path);
        }
      },
    });
  };

  if (isMobileBreakpoint) {
    return (
      <Menu isLazy>
        <MenuButton
          as={IconButton}
          colorScheme="dark"
          icon={<Icon icon="Menu" size="5" />}
          ml="-2"
          size="sm"
          variant="ghost"
        />
        <MenuList fontSize="md">
          <MenuItem
            icon={<Icon icon="User" />}
            onClick={handlePersonalAccountClick}
          >
            {personalAccountName}
          </MenuItem>
          {sharedOrganizations.map((org) => (
            <MenuItem
              command={getRoleLabel(org.role)}
              icon={<Icon icon={getOrgTypeIcon(org.type)} />}
              key={org.id}
              onClick={() => changeTab('organizations', org.id)}
            >
              {org.name}
            </MenuItem>
          ))}
          <MenuItem
            icon={<Icon icon="Plus" />}
            onClick={handleCreateOrganizationClick}
          >
            {t('navigation.create_organization')}
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={<Icon icon="User" />} onClick={handleAccountClick}>
            {t('navigation.account')}
          </MenuItem>
          <MenuItem
            icon={<Icon icon="Calendar" />}
            onClick={handleCalendarsClick}
          >
            {t('navigation.calendars')}
          </MenuItem>
          <MenuItem
            icon={<Icon icon="Image" />}
            onClick={handleIntegrationsClick}
          >
            {t('navigation.integrations')}
          </MenuItem>
          <MenuItem icon={<Icon icon="HelpCircle" />} onClick={handleHelpClick}>
            {t('navigation.help')}
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={<Icon icon="Logout" />} onClick={handleLogoutClick}>
            {t('auth:sign_out')}
          </MenuItem>
        </MenuList>
      </Menu>
    );
  }

  return (
    <>
      <Text pb="1" pt="1" px="3" textStyle="ahLabel">
        {t('navigation.accounts_heading')}
      </Text>
      <AccountListItem
        icon="User"
        isActive={tab === 'subscription'}
        name={personalAccountName}
        subtitle={t('navigation.personal_account')}
        onClick={handlePersonalAccountClick}
      />
      {sharedOrganizations.map((org) => (
        <AccountListItem
          icon={getOrgTypeIcon(org.type)}
          isActive={tab === 'organizations' && organizationId === org.id}
          key={org.id}
          name={org.name}
          role={getRoleLabel(org.role)}
          subtitle={getOrgSubtitle(org.type)}
          onClick={() => changeTab('organizations', org.id)}
        />
      ))}
      <CreateOrganizationItem
        isActive={tab === 'createOrganization'}
        label={t('navigation.create_organization')}
        onClick={handleCreateOrganizationClick}
      />

      <Divider my="4" />

      <Tab
        isActive={tab === 'account'}
        leftIcon={<Icon icon="User" />}
        onClick={handleAccountClick}
      >
        {t('navigation.account')}
      </Tab>
      <Tab
        isActive={tab === 'calendars'}
        leftIcon={<Icon icon="Calendar" />}
        onClick={handleCalendarsClick}
      >
        {t('navigation.calendars')}
      </Tab>
      <Tab
        isActive={tab === 'integrations'}
        leftIcon={<Icon icon="Image" />}
        onClick={handleIntegrationsClick}
      >
        {t('navigation.integrations')}
      </Tab>
      <Tab
        isActive={tab === 'help'}
        leftIcon={<Icon icon="HelpCircle" />}
        onClick={handleHelpClick}
      >
        {t('navigation.help')}
      </Tab>

      <Divider my="4" />

      <Button
        justifyContent="flex-start"
        leftIcon={<Icon icon="Logout" />}
        pl="4"
        variant="link"
        onClick={handleLogoutClick}
      >
        {t('auth:sign_out')}
      </Button>
    </>
  );
};
